import { createAction } from 'redux-action';
import axios from 'axios';
import { AxiosCallError, AxiosCallRequest, AxiosCallSuccess } from './commonActions';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { setProblemDetails, setProblems, setProblemTestCases } from '../reducers/ProblemReducer.js';
import { DEFAULT_TEMPLATES, FREE_FORM_EDITOR_ID } from '../../const';

let ep = get_service_endpoint('interview');

export const setLastCode = createAction('SET_LAST_CODE');
export const setLastLanguage = createAction('SET_LAST_LANGUAGE');
export const setChatEvents = createAction('SET_CHAT_EVENTS');
export const setUnreadCount = createAction('SET_UNREAD_COUNT');
export const getSupportedLanguages = createAction('GET_SUPPORTED_LANGUAGES');
export const setNotes = createAction('SET_NOTES');
export const updateNote = createAction('UPDATE_NOTE');
export const setInitialNotes = createAction('SET_INITIAL_NOTES');
export const setEndInterviewUserDetail = createAction('SET_END_INTERVIEW_USER_DETAIL');
export const getInterviewSuccess = createAction('GET_INTERVIEW_SUCCESS');
export const setParticipants = createAction('SET_PARTICIPANTS');
export const setHeartbeat = createAction('SET_HEARTBEAT');
export const setResults = createAction('SET_RESULTS');
export const setActiveTab = createAction('SET_ACTIVE_TAB');
export const setLocalMute = createAction('SET_LOCAL_MUTE');
export const setRemoteMute = createAction('SET_REMOTE_MUTE');
export const setLoadingResults = createAction('SET_LOADING_RESULTS');
export const setCustomInputTracking = createAction('SET_CUSTOM_INPUT_TRACKING');
export const clearCustomInputTracking = createAction('CLEAR_CUSTOM_INPUT_TRACKING');
export const setCurrentLanguageforProblems = createAction('SET_CURRENT_LANGUAGE_FOR_PROBLEMS');
export const updateLanguageForProblem = createAction('UPDATE_LANGUAGE_FOR_PROBLEM');
export const updateCodeAndLanguageForProblem = createAction('UPDATE_CODE_AND_LANGUAGE_FOR_PROBLEM');

export const submitInterviewCode = (code, language, problem_id, data) => {
  return (dispatch, getState) => {
    dispatch(AxiosCallRequest());
    let interview_id = getState().interview.interview_id;
    let url = `${ep}/api/interviews/${interview_id}/coding-problems/${problem_id}/submissions/`;

    if (data.type === 'custom_input') {
      dispatch(setCustomInputTracking({ type: data.type }));
    } else if (data.type !== 'custom_input') {
      dispatch(clearCustomInputTracking());
    }

    axios
      .post(url, {
        code_body: code,
        language: language,
        cs_type: data.type,
        ...(data.type === 'custom_input' && { ci_stdin: data.ci_stdin }),
      })
      .then(res => {
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const submitFreeFormCode = (code, language) => {
  return (dispatch, getState) => {
    let interview_id = getState().interview.interview_id;
    let url = `${ep}/api/interview/${interview_id}/code_submissions/`;

    axios
      .post(url, {
        code,
        language,
      })
      .then(res => {
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const fetchLatestEditorEvent = (interview_id, language, problem_id, templateBody) => {
  return dispatch => {
    const baseUrl = `${ep}/api/interview/${interview_id}/editor_events/latest/`;
    const params = new URLSearchParams();
    if (language) {
      params.append('language', language);
      if (problem_id && problem_id !== 'free_form_editor') {
        params.append('problem_id', problem_id);
      }
    }
    const url = `${baseUrl}?${params.toString()}`;
    return axios
      .get(url)
      .then(res => {
        if (res.data.msg !== 'Does Not Exist') {
          dispatch(
            updateLanguageForProblem({
              problem_id: res.data.problem_id ? res.data.problem_id : FREE_FORM_EDITOR_ID,
              language: res.data.language,
              code: res.data.data,
            }),
          );
        } else {
          // If templateBody is empty, get template from DEFAULT_TEMPLATES by language
          const finalTemplateBody =
            templateBody ||
            (language ? DEFAULT_TEMPLATES.find(t => t.language === language)?.code || '' : '');
          dispatch(updateLanguageForProblem({ problem_id, language, code: finalTemplateBody }));
        }
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const fetchChatEvents = interview_id => {
  return dispatch => {
    let url = `${ep}/api/interview/${interview_id}/chat_events/`;
    return axios
      .get(url)
      .then(res => {
        dispatch(setChatEvents(res.data));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const fetchNotes = interview_id => {
  return dispatch => {
    let url = `${ep}/api/interview/${interview_id}/user_notes/`;
    return axios
      .get(url)
      .then(res => {
        dispatch(setInitialNotes(res.data));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
        dispatch(setInitialNotes([]));
      });
  };
};

export const fetchData = interview_id => {
  let promise_array = [];
  return (dispatch, getState) => {
    dispatch(AxiosCallRequest());
    promise_array.push(dispatch(fetchChatEvents(interview_id)));
    if (localStorage.getItem('role') === 'IN')
      promise_array.push(dispatch(fetchNotes(interview_id)));
    dispatch(AxiosCallSuccess());
    return Promise.all(promise_array);
  };
};

export const getLanguages = () => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let ep1 = get_service_endpoint('copdg');
    let url = `${ep1}/api/supported_languages/`;
    axios
      .get(url)
      .then(response => {
        dispatch(getSupportedLanguages(response.data.languages));
        dispatch(AxiosCallSuccess());
      })
      .catch(err => {
        dispatch(AxiosCallError(err));
      });
  };
};

export const logout = (shouldClearSessionData = true) => {
  return dispatch => {
    if (shouldClearSessionData) {
      localStorage.clear();
      delete axios.defaults.headers.common['X-Access-Token'];
      delete axios.defaults.headers.common['X-Refresh-Token'];
    }
  };
};

export const postNote = (data, callback) => {
  return (dispatch, getState) => {
    let interview_id = getState().interview.interview_id;
    let url = `${ep}/api/interview/${interview_id}/user_notes/`;
    axios
      .post(url, { data })
      .then(response => {
        dispatch(setNotes(response.data));
        if (callback) callback();
      })
      .catch(err => {
        dispatch(AxiosCallError(err));
        if (callback) callback();
      });
  };
};

export const editNote = (note_id, fire_time, data) => {
  return (dispatch, getState) => {
    let interview_id = getState().interview.interview_id;
    let url = `${ep}/api/interview/${interview_id}/note?fire_time=${fire_time}&id=${note_id}`;
    axios
      .patch(url, { data })
      .then(response => {
        dispatch(updateNote(response.data));
      })
      .catch(err => {
        dispatch(AxiosCallError(err));
      });
  };
};

export const deleteNote = (note_id, fire_time) => {
  return (dispatch, getState) => {
    let interview_id = getState().interview.interview_id;
    let url = `${ep}/api/interview/${interview_id}/note?fire_time=${fire_time}&id=${note_id}`;
    axios
      .delete(url)
      .then(response => {
        dispatch(fetchNotes(interview_id));
      })
      .catch(err => {
        dispatch(AxiosCallError(err));
      });
  };
};

export const submitFeedback = (data, callback) => {
  return (dispatch, getState) => {
    let email_id = localStorage.getItem('username');
    let participants = getState().interview.participants;
    let participant_id = participants.find(item => item.email === email_id).id;
    let url = `${ep}/api/participant/${participant_id}/feedback/`;
    axios
      .patch(url, data)
      .then(response => {
        if (callback) callback();
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const getInterview = interview_id => {
  return (dispatch, getState) => {
    let url = `${ep}/api/interview/${interview_id}/`;
    return axios
      .get(url)
      .then(response => {
        dispatch(getInterviewSuccess(response.data));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const checkNetworkConnection = () => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let ep = get_service_endpoint('copdg');
    let url = `${ep}/api/supported_languages/`;
    return axios.get(url);
  };
};

export const getProblems = () => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let ep = get_service_endpoint('copdg');
    let problemsUrl = `${ep}/api/problems/?creater=system`;
    let tagsUrl = `${ep}/api/tags/`;
    Promise.all([axios.get(problemsUrl), axios.get(tagsUrl)])
      .then(([problemsResponse, tagsResponse]) => {
        dispatch(AxiosCallSuccess());
        dispatch(setProblems({ problems: problemsResponse.data, tags: tagsResponse.data }));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const getProblemTestCases = problem_id => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let ep = get_service_endpoint('copdg');
    let url = `${ep}/api/problems/${problem_id}/test_cases/`;
    axios
      .get(url)
      .then(response => {
        dispatch(AxiosCallSuccess());
        dispatch(setProblemTestCases(response.data));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const getCodingProblems = interview_id => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/interviews/${interview_id}/coding-problems/?include_details=true`;
    axios
      .get(url)
      .then(response => {
        dispatch(AxiosCallSuccess());
        dispatch(setProblemDetails(response.data));
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

export const addCodingProblem = (interview_id, problem_id, visibility, order, points) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/interviews/${interview_id}/coding-problems/`;
    return axios
      .post(url, {
        problem_id: `${problem_id}`,
        state: 'IN',
        visibility_status: visibility,
        order: order,
        points: `${points}`,
      })
      .then(response => {
        dispatch(AxiosCallSuccess());
        return response.data;
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
        throw error;
      });
  };
};

export const updateCodingProblemVisibility = (interview_id, problemId, visibility) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/interviews/${interview_id}/coding-problems/${problemId}/`;
    return axios
      .put(url, {
        visibility_status: visibility,
      })
      .then(response => {
        dispatch(AxiosCallSuccess());
        return response.data;
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
        throw error;
      });
  };
};

export const deleteCodingProblem = (interview_id, problemId) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    let url = `${ep}/api/interviews/${interview_id}/coding-problems/${problemId}/`;
    return axios
      .delete(url)
      .then(response => {
        dispatch(AxiosCallSuccess());
        return response.data;
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
        throw error;
      });
  };
};

export const subscribeToRemoteAudio = () => {
  return (dispatch, getState) => {
    const { meetingSession } = getState().interviewRecording;
    const participants = getState().interview.participants || [];

    if (!meetingSession || !meetingSession.audioVideo) return;

    const sessionAudioVideo = meetingSession.audioVideo;

    // Subscribe to remote participant volume/mute status
    const remoteCallbacks = participants.map(p => {
      const cb = (attendeeId, volume, muted) => {
        dispatch(setRemoteMute({ attendeeId, muted }));
      };
      sessionAudioVideo.realtimeSubscribeToVolumeIndicator(p.attendeeId, cb);
      return { attendeeId: p.attendeeId, callback: cb };
    });

    // Return unsubscribe function
    return () => {
      remoteCallbacks.forEach(({ attendeeId, callback }) => {
        sessionAudioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId, callback);
      });
    };
  };
};
